import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Mail } from 'lucide-react';
import axios from 'axios';
import { useSiteImage } from '../lib/siteAssets';
import { usePageSeo } from '../lib/seo';
import { track } from '../lib/analytics';
const FREEBIE_DEFAULT = 'https://static.prod-images.emergentagent.com/jobs/6fa42141-fdc2-4ef8-946b-3af9a9eb53ac/images/d309224d17de74b4346465469ff119aa3783e15719579c88b63242108c942999.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FreebiePage = () => {
  usePageSeo('freebie', { title: 'Free Relocation Checklist', description: 'Download the free relocation checklist for Australians moving to Europe: every step from visas to shipping, in order.' });
  const freebieSrc = useSiteImage('freebie_image', FREEBIE_DEFAULT);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email,
        name: name || null
      });
      
      if (response.data.success) {
        track('generate_lead', { lead_source: 'freebie_checklist' });
        track('sign_up', { method: 'newsletter' });
        setIsSuccess(true);
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Subscription error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const benefits = [
    'The visa pitfalls everyone overlooks',
    'Hidden costs that catch Australians off guard',
    'Cultural differences that will surprise you',
    'Banking and healthcare insider tips',
    'Pet relocation essentials',
    'What to sell, ship, or leave behind'
  ];

  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="order-2 lg:order-1"
          >
            <div className="relative">
              <img
                src={freebieSrc}
                alt="Free Guide Preview"
                className="w-full max-w-md mx-auto rounded-3xl shadow-2xl"
              />
              {/* Floating testimonial */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-4 max-w-xs hidden md:block"
              >
                <p className="text-sm text-[#1E1622]/80 italic mb-2">
                  "This guide saved me from making expensive mistakes!"
                </p>
                <p className="text-xs font-semibold text-[#3B164C]">— Sarah M., Sydney</p>
              </motion.div>
            </div>
          </motion.div>

          {/* Form Side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="order-1 lg:order-2"
          >
            <span className="inline-block text-[#C5A059] text-sm font-medium tracking-wider uppercase mb-4">
              Free Download
            </span>
            <h1 
              data-testid="freebie-headline"
              className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1E1622] leading-tight mb-4"
            >
              10 Things Nobody Tells You About Moving to Italy
            </h1>
            <p className="text-lg text-[#1E1622]/70 leading-relaxed mb-8">
              The lessons we learned the hard way — so you don't have to.
            </p>

            {/* Benefits List */}
            <div className="mb-8">
              <p className="font-medium text-[#1E1622] mb-4">Inside this free guide:</p>
              <ul className="grid sm:grid-cols-2 gap-3">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3 text-sm text-[#1E1622]/80">
                    <span className="w-5 h-5 rounded-full bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={12} className="text-[#C5A059]" />
                    </span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            {/* Form or Success Message */}
            {isSuccess ? (
              <motion.div
                {...fadeUp}
                data-testid="freebie-success"
                className="bg-[#C5A059]/10 rounded-2xl p-8 text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
                  <Mail size={28} className="text-[#C5A059]" />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-[#1E1622] mb-2">
                  Check your inbox!
                </h3>
                <p className="text-[#1E1622]/70 mb-6">
                  Your free guide is on its way. Keep an eye out for an email from The Expat Studio.
                </p>
                <Link 
                  to="/shop" 
                  data-testid="freebie-success-shop-link"
                  className="inline-flex items-center gap-2 text-[#3B164C] font-medium hover:gap-3 transition-all"
                >
                  Browse our complete Italy Relocation Kit
                  <ArrowRight size={18} />
                </Link>
              </motion.div>
            ) : (
              <form 
                onSubmit={handleSubmit}
                data-testid="freebie-form"
                className="space-y-6"
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#1E1622] mb-2">
                    Name (optional)
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="freebie-name-input"
                    placeholder="Your name"
                    className="input-editorial"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#1E1622] mb-2">
                    Email address <span className="text-[#3B164C]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="freebie-email-input"
                    placeholder="you@example.com"
                    required
                    className="input-editorial"
                  />
                </div>

                {error && (
                  <p data-testid="freebie-error" className="text-red-500 text-sm">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="freebie-submit"
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Me the Free Guide
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                <p className="text-xs text-[#1E1622]/50 text-center">
                  By signing up, you agree to receive emails from The Expat Studio. 
                  We respect your privacy and you can unsubscribe at any time.
                </p>
              </form>
            )}
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div 
          {...fadeUp}
          data-testid="freebie-bottom-cta"
          className="mt-20 text-center"
        >
          <p className="text-[#1E1622]/70 mb-4">
            Love this? Check out our complete Italy Relocation Kit
          </p>
          <Link 
            to="/shop" 
            className="btn-secondary inline-flex items-center gap-2"
          >
            View Relocation Kits
            <ArrowRight size={18} />
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default FreebiePage;
